<template>
  <div>
    <div class="audio-waveform">
      <div id="spinner" v-if="loading" />
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else class="waveform-flex-container">
        <!-- button -->
        <div class="control-button">
          <i
            class="bxr play-icon"
            :class="{ 'bxs-pause-circle': isPlaying, 'bxs-play-circle': !isPlaying }"
            @click="togglePlayback"
          ></i>
        </div>
        <!-- canvas -->
        <div class="waveform-container">
          <canvas ref="waveformCanvas" class="waveform-canvas" @click="seekToPosition"></canvas>
        </div>
        <!-- time -->
        <div class="total-time" :class="{ playing: isPlaying }">
          {{ isPlaying ? formatTime(currentTime) : formatTime(duration) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'

// Define props with default values
const props = defineProps<{
  audioUrl: string
}>()

const emit = defineEmits(['loaded', 'error', 'play', 'pause', 'ended'])

// Refs
const waveformCanvas = ref<HTMLCanvasElement | null>(null)
const audioContext = ref<AudioContext | null>(null)
const audioSource = ref<AudioBufferSourceNode | null>(null)
const audioBuffer = ref<AudioBuffer | null>(null)
const animationId = ref<number | null>(null)
const isPlaying = ref(false)
const loading = ref(true)
const error = ref<string | null>(null)
const currentTime = ref(0)
const duration = ref(0)
const startTime = ref(0)
const waveformData = ref<number[]>([])

// Initialize audio on component mount
onMounted(() => {
  initAudio()
})

// Clean up resources on component unmount
onBeforeUnmount(() => {
  cleanup()
})

// Watch for URL changes
watch(
  () => props.audioUrl,
  () => {
    cleanup()
    initAudio()
  },
)

// Initialize audio context and load audio file
const initAudio = async () => {
  try {
    loading.value = true
    error.value = null

    // Create audio context
    audioContext.value = new AudioContext()

    // Fetch audio data
    const response = await fetch(props.audioUrl)
    if (!response.ok) {
      throw new Error(`Failed to load audio: ${response.statusText}`)
    }

    const arrayBuffer = await response.arrayBuffer()
    audioBuffer.value = await audioContext.value.decodeAudioData(arrayBuffer)
    duration.value = audioBuffer.value.duration
    loading.value = false

    nextTick(() => {
      // Generate waveform data
      generateWaveformData()
      // Draw initial waveform
      drawWaveform()
      emit('loaded', { duration: duration.value })
    })
  } catch (err: unknown) {
    console.error('Error initializing audio:', err)
    const errorMessage = err instanceof Error ? err.message : 'Failed to load audio'
    error.value = errorMessage
    loading.value = false
    emit('error', err)
  }
}

// Generate waveform data from audio buffer
const generateWaveformData = () => {
  if (!audioBuffer.value) return

  const rawData = audioBuffer.value.getChannelData(0) // Use first channel

  // Get current canvas width
  const canvasWidth = waveformCanvas.value?.width || 300

  // Limit to maximum 50 bars regardless of width
  const maxBars = 100
  const samples = Math.min(maxBars, Math.floor(canvasWidth * 0.15))

  const blockSize = Math.floor(rawData.length / samples) // Number of samples in each data point
  const tempData = []
  let absoluteMax = 0 // Track the absolute maximum value for normalization

  // First pass: find all peak values and the absolute max
  for (let i = 0; i < samples; i++) {
    const blockStart = blockSize * i
    let maxValue = 0

    // Find the max value in this block (peak amplitude)
    for (let j = 0; j < blockSize; j++) {
      const sample = Math.abs(rawData[blockStart + j] || 0)
      if (sample > maxValue) {
        maxValue = sample
      }
    }

    // Add slight variation to avoid perfectly uniform look
    const randomFactor = 0.85 + Math.random() * 0.3
    const value = maxValue * randomFactor

    tempData.push(value)

    // Keep track of the absolute maximum value across all blocks
    if (value > absoluteMax) {
      absoluteMax = value
    }
  }

  // Second pass: normalize all values relative to the absolute max
  // This ensures the highest peak is at 100% and others are scaled proportionately
  const filteredData = tempData.map((value) => {
    if (absoluteMax === 0) return 0.05 // Prevent division by zero

    // Normalize to 0-1 range based on the max value
    // Also set a minimum height for very quiet sections
    return Math.max(0.05, value / absoluteMax)
  })

  waveformData.value = filteredData
}

// Toggle between play and pause
const togglePlayback = () => {
  if (isPlaying.value) {
    pauseAudio()
  } else {
    playAudio()
  }
}

// Click on waveform to seek to position
const seekToPosition = (event: MouseEvent) => {
  if (!audioBuffer.value) return

  const canvas = waveformCanvas.value
  if (!canvas) return

  const rect = canvas.getBoundingClientRect()
  const x = event.clientX - rect.left
  const seekPosition = (x / canvas.width) * duration.value

  currentTime.value = Math.max(0, Math.min(seekPosition, duration.value))

  if (isPlaying.value) {
    pauseAudio()
  } else {
    drawWaveform()
  }
  playAudio()
}

// Start audio playback
const playAudio = () => {
  if (!audioBuffer.value || isPlaying.value || !audioContext.value) return

  // Create new source (can't reuse old ones)
  audioSource.value = audioContext.value.createBufferSource()
  audioSource.value.buffer = audioBuffer.value

  // Connect nodes
  audioSource.value.connect(audioContext.value.destination)

  // Handle completion
  audioSource.value.onended = () => {
    isPlaying.value = false
    currentTime.value = 0
    if (animationId.value !== null) {
      cancelAnimationFrame(animationId.value)
    }
    drawWaveform()
    emit('ended')
  }

  // Start playback
  const offset = currentTime.value
  audioSource.value.start(0, offset)
  startTime.value = audioContext.value.currentTime - offset
  isPlaying.value = true
  emit('play')

  // Start animation to update progress
  updateProgress()
}

// Pause audio playback
const pauseAudio = () => {
  if (!isPlaying.value || !audioSource.value || !audioContext.value) return

  // Stop playback
  audioSource.value.stop()
  currentTime.value = audioContext.value.currentTime - startTime.value
  isPlaying.value = false

  // Stop animation
  if (animationId.value !== null) {
    cancelAnimationFrame(animationId.value)
  }
  emit('pause', { currentTime: currentTime.value })
}

// Update progress position during playback
const updateProgress = () => {
  if (!isPlaying.value || !audioContext.value) return

  // Update current time
  currentTime.value = audioContext.value.currentTime - startTime.value

  // Redraw waveform to update the bar colors (not on every frame to improve performance)
  // We redraw around 10 times per second which is enough for visual feedback
  if (Math.random() < 0.3) {
    // 30% chance per frame to update
    drawWaveform()
  }

  // Continue animation loop if playing
  if (isPlaying.value && currentTime.value < duration.value) {
    animationId.value = requestAnimationFrame(updateProgress)
  } else if (currentTime.value >= duration.value) {
    // Ensure we catch the end of playback correctly
    currentTime.value = duration.value
    drawWaveform()
  }
}

// Draw static waveform
const drawWaveform = () => {
  const canvas = waveformCanvas.value
  if (!canvas) {
    console.log('Canvas not found')
    return
  }

  const canvasCtx = canvas.getContext('2d')
  if (!canvasCtx) return

  // Get colors from CSS variables with fallback to props
  const computedStyle = window.getComputedStyle(canvas)
  const backgroundColor =
    computedStyle.getPropertyValue('--waveform-background-color').trim() || 'transparent'
  const waveColor = computedStyle.getPropertyValue('--waveform-bar-color').trim() || '#6a7388' // Default blue color
  const progressColor =
    computedStyle.getPropertyValue('--waveform-progress-color').trim() || '#FF7B30' // Default orange color

  // Clear canvas
  canvasCtx.fillStyle = backgroundColor
  canvasCtx.clearRect(0, 0, canvas.width, canvas.height)
  canvasCtx.fillRect(0, 0, canvas.width, canvas.height)

  // Calculate bar dimensions
  const barCount = waveformData.value.length

  // Distribute bars evenly across the full canvas width
  // This ensures bars are evenly spaced even with fewer bars
  const totalWidth = canvas.width
  const totalBarWidthWithSpacing = totalWidth / barCount
  const barWidth = totalBarWidthWithSpacing * 0.5 // Bar width is 50% of available space (increased spacing)

  // Calculate current progress position for coloring
  const progress = duration.value > 0 ? currentTime.value / duration.value : 0
  const progressBarIndex = Math.floor(progress * barCount)

  // Draw each bar
  for (let i = 0; i < barCount; i++) {
    const value = waveformData.value[i]
    // Each value is already normalized (0-1), so we can use it directly with the canvas height
    // We use 95% of the height to leave a small margin at the top and bottom
    const barHeight = value * canvas.height * 0.95

    // Position calculations - center the bar vertically
    const x = i * totalBarWidthWithSpacing
    const y = (canvas.height - barHeight) / 2

    // Determine color based on progress
    canvasCtx.fillStyle = i <= progressBarIndex ? progressColor : waveColor

    // Draw the bar with rounded corners
    const cornerRadius = Math.min(barWidth / 2, 4) // Radius of the rounded corners, max 4px

    // Create a rounded rectangle path
    canvasCtx.beginPath()
    canvasCtx.moveTo(x + cornerRadius, y)
    canvasCtx.lineTo(x + barWidth - cornerRadius, y)
    canvasCtx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + cornerRadius)
    canvasCtx.lineTo(x + barWidth, y + barHeight - cornerRadius)
    canvasCtx.quadraticCurveTo(
      x + barWidth,
      y + barHeight,
      x + barWidth - cornerRadius,
      y + barHeight,
    )
    canvasCtx.lineTo(x + cornerRadius, y + barHeight)
    canvasCtx.quadraticCurveTo(x, y + barHeight, x, y + barHeight - cornerRadius)
    canvasCtx.lineTo(x, y + cornerRadius)
    canvasCtx.quadraticCurveTo(x, y, x + cornerRadius, y)
    canvasCtx.closePath()

    // Fill the rounded rectangle
    canvasCtx.fill()
  }
}

// Format seconds to MM:SS
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`
}

// Clean up resources
const cleanup = () => {
  if (audioSource.value) {
    try {
      audioSource.value.stop()
    } catch {
      // Ignore if already stopped
    }
    audioSource.value.disconnect()
  }

  if (audioContext.value && audioContext.value.state !== 'closed') {
    audioContext.value.close()
  }

  if (animationId.value !== null) {
    cancelAnimationFrame(animationId.value)
  }

  audioContext.value = null
  audioSource.value = null
  audioBuffer.value = null
  isPlaying.value = false
  currentTime.value = 0
  duration.value = 0
}
</script>
